import { useState, useEffect } from 'react';
import { Plus, Trash2, Save, Star, AlertCircle } from 'lucide-react';

import { API_URL } from '../../config/api';

const MAX_FEATURED = 6;
const CHAR_LIMITS = {
    name: 80,
    description: 200,
    category: 40
};

const TestsManager = () => {
    const [tests, setTests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [meta, setMeta] = useState({ featured_count: 0, can_feature_more: true });
    const [editingTest, setEditingTest] = useState(null);
    const [showForm, setShowForm] = useState(false);

    useEffect(() => {
        fetchTests();
    }, []);

    const fetchTests = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/genetic-tests/all`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) {
                setTests(data.data || []);
                setMeta(data.meta || {});
            }
        } catch (error) {
            setMessage({ type: 'error', text: 'Error al cargar pruebas' });
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (test) => {
        setSaving(true);
        try {
            const token = localStorage.getItem('token');
            const isNew = !test.id;
            const url = isNew ? `${API_URL}/genetic-tests` : `${API_URL}/genetic-tests/${test.id}`;

            const response = await fetch(url, {
                method: isNew ? 'POST' : 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(test)
            });

            const data = await response.json();
            if (data.success) {
                setMessage({ type: 'success', text: 'Guardado' });
                fetchTests();
                setShowForm(false);
                setEditingTest(null);
            } else {
                setMessage({ type: 'error', text: data.message });
            }
        } catch (error) {
            setMessage({ type: 'error', text: 'Error al guardar' });
        } finally {
            setSaving(false);
            setTimeout(() => setMessage({ type: '', text: '' }), 3000);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('¿Eliminar esta prueba?')) return;
        try {
            const token = localStorage.getItem('token');
            await fetch(`${API_URL}/genetic-tests/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            fetchTests();
        } catch (error) {
            setMessage({ type: 'error', text: 'Error al eliminar' });
        }
    };

    const toggleFeatured = async (test) => {
        if (!test.is_featured && !meta.can_feature_more) {
            setMessage({ type: 'error', text: `Máximo ${MAX_FEATURED} pruebas destacadas` });
            return;
        }
        await handleSave({ ...test, is_featured: !test.is_featured });
    };

    const CharCounter = ({ value, limit }) => {
        const count = (value || '').length;
        const pct = (count / limit) * 100;
        return (
            <span className={`char-counter ${pct > 90 ? 'warning' : ''} ${pct >= 100 ? 'error' : ''}`}>
                {count}/{limit}
            </span>
        );
    };

    const TestForm = ({ test, onSave, onCancel }) => {
        const [formData, setFormData] = useState(test || {
            name: '', description: '', category: '', price: '', turnaround_days: '', is_featured: false, is_active: true
        });

        return (
            <div className="item-form">
                <div className="form-grid">
                    <div className="form-group">
                        <label>Nombre <CharCounter value={formData.name} limit={CHAR_LIMITS.name} /></label>
                        <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value.slice(0, CHAR_LIMITS.name) })}
                            placeholder="Nombre de la prueba"
                        />
                    </div>
                    <div className="form-group">
                        <label>Categoría <CharCounter value={formData.category} limit={CHAR_LIMITS.category} /></label>
                        <input
                            type="text"
                            value={formData.category}
                            onChange={(e) => setFormData({ ...formData, category: e.target.value.slice(0, CHAR_LIMITS.category) })}
                            placeholder="Ej: Citogenética"
                        />
                    </div>
                    <div className="form-group full-width">
                        <label>Descripción <CharCounter value={formData.description} limit={CHAR_LIMITS.description} /></label>
                        <textarea
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value.slice(0, CHAR_LIMITS.description) })}
                            rows={2}
                            placeholder="Breve descripción"
                        />
                    </div>
                    <div className="form-group">
                        <label>Precio (MXN)</label>
                        <input
                            type="number"
                            value={formData.price}
                            onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                            placeholder="0.00"
                        />
                    </div>
                    <div className="form-group">
                        <label>Días de entrega</label>
                        <input
                            type="number"
                            value={formData.turnaround_days}
                            onChange={(e) => setFormData({ ...formData, turnaround_days: e.target.value })}
                            placeholder="15"
                        />
                    </div>
                    <div className="form-group checkbox-row">
                        <label>
                            <input type="checkbox" checked={formData.is_featured} onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })} />
                            Destacada en homepage
                        </label>
                        <label>
                            <input type="checkbox" checked={formData.is_active} onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })} />
                            Activa
                        </label>
                    </div>
                </div>
                <div className="form-actions">
                    <button className="btn btn-secondary" onClick={onCancel}>Cancelar</button>
                    <button className="btn btn-primary" onClick={() => onSave(formData)} disabled={saving || !formData.name}>
                        <Save size={16} /> {saving ? 'Guardando...' : 'Guardar'}
                    </button>
                </div>
            </div>
        );
    };

    if (loading) return <div className="loading-state">Cargando pruebas...</div>;

    return (
        <div className="tests-manager">
            <div className="manager-header">
                <div className="header-info">
                    <h3>Pruebas Genéticas</h3>
                    <span className="count-badge">{meta.featured_count || 0} / {MAX_FEATURED} destacadas</span>
                </div>
                <button className="btn btn-primary" onClick={() => { setEditingTest(null); setShowForm(true); }}>
                    <Plus size={16} /> Agregar Prueba
                </button>
            </div>

            {message.text && <div className={`message ${message.type}`}>{message.text}</div>}
            {showForm && <TestForm test={editingTest} onSave={handleSave} onCancel={() => { setShowForm(false); setEditingTest(null); }} />}

            <div className="items-table">
                <table>
                    <thead>
                        <tr>
                            <th>Nombre</th>
                            <th>Categoría</th>
                            <th>Precio</th>
                            <th>Días</th>
                            <th>Estado</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {tests.map((test) => (
                            <tr key={test.id} className={!test.is_active ? 'inactive' : ''}>
                                <td>
                                    {test.is_featured && <Star size={14} className="featured-icon" />}
                                    {test.name}
                                </td>
                                <td>{test.category}</td>
                                <td>${test.price?.toLocaleString() || '-'}</td>
                                <td>{test.turnaround_days || '-'}</td>
                                <td>
                                    <span className={`status-badge ${test.is_active ? 'active' : 'inactive'}`}>
                                        {test.is_active ? 'Activa' : 'Oculta'}
                                    </span>
                                </td>
                                <td className="actions-cell">
                                    <button onClick={() => toggleFeatured(test)} title="Destacar">
                                        <Star size={16} className={test.is_featured ? 'filled' : ''} />
                                    </button>
                                    <button onClick={() => { setEditingTest(test); setShowForm(true); }}>✏️</button>
                                    <button onClick={() => handleDelete(test.id)} className="danger"><Trash2 size={16} /></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default TestsManager;
